import React, {useEffect, useState} from 'react'
import {
    Box,
    Text,
    Link,
    Image,
    Skeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {useStoreContext} from '../../../contexts/store'
import {FormattedMessage} from 'react-intl'
import {getUrl} from '../../../utils/cloudinary-utils'
import {AdyenShippingMethodsService} from '../../../adyen/lib/services/shipping-methods'
import {fetchShippingMethods} from '../../../adyen/lib/contexts/adyen-express-checkout-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getStoreAddress} from '../../../utils/common-utils'
import {useAccessToken, useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {DEFAULT_SHIPMENT_ID} from '@salesforce/retail-react-app/app/constants'
import {useQueryClient} from '@tanstack/react-query'
import LoadingSpinner from '../../../components/loading-spinner'
import {useGlobaleContext, useResolvedCountryISO} from '../../../contexts/globale'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

const CartDeliveryOptions = ({
    selectedDelivery,
    setDelivery,
    getGlobalePrices,
    basket,
    setShowPriceSkeleton,
    getBOPISAvailability,
    setisBopisLoading,
    clearBopisFlag,
    oosBopisSkus = [],
    hasBopisShipmentWithEngravingProduct = false
}) => {
    const {countryISO, setRefreshPrices, currencyCode} = useGlobaleContext()
    const resolvedCountryISO = useResolvedCountryISO()
    const [showSpinner, setShowSpinner] = useState(false)
    const {getTokenWhenReady} = useAccessToken()
    const queryClient = useQueryClient()
    const updateShipmentForBasketMutation = useShopperBasketsMutation('updateShipmentForBasket')
    const {site} = useMultiSite()
    const cartStyles = useMultiStyleConfig('CartStyles')
    const {store, onOpen, refreshBopisCart, setRefreshBopisCart} = useStoreContext()

    const mutateShipping = async (isBopis) => {
        if (!basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled && !isBopis) {
            // No need to update this if we are on cart page,
            // method is already non-bopis and user is changing it to non-bopis.
            return
        }
        try {
            let previousAddress = !basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled
                ? basket?.shipments?.[0]?.shippingAddress
                : null
            if (previousAddress) {
                delete previousAddress.id
            }
            await updateShipmentForBasketMutation.mutateAsync({
                parameters: {
                    basketId: basket.basketId,
                    shipmentId: DEFAULT_SHIPMENT_ID
                },
                body: {
                    shippingAddress: isBopis
                        ? getStoreAddress(store)
                        : {
                              ...(previousAddress || {}), // Note: We want to retain the previous address that user entered in checkout and that is in user's basket
                              countryCode: countryISO
                          },
                    c_fromStoreId: isBopis ? store?.ID || store?.id : '',
                    c_shipmentType: isBopis ? 'instore' : ''
                }
            })
        } catch (e) {
            console.error('Unable to mutate address', e.message)
        }
    }
    const changeShippingInformation = async (isBopis) => {
        const token = await getTokenWhenReady()
        const adyenShippingMethodsService = new AdyenShippingMethodsService(token, site)
        setShowPriceSkeleton(true)
        setShowSpinner(true)

        try {
            if (basket?.productItems?.length > 0) {
                await mutateShipping(isBopis)
            }
            const {applicableShippingMethods, defaultShippingMethodId} = await fetchShippingMethods(
                basket?.basketId,
                site,
                token,
                isBopis
            )
            const methodIdToUse =
                isBopis || countryISO !== 'US'
                    ? applicableShippingMethods?.[0]?.id
                    : basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled ||
                      basket?.shipments?.[0]?.shippingMethod?.c_isGeShippingMethod
                    ? defaultShippingMethodId
                    : basket?.shipments?.[0]?.shippingMethod?.id || defaultShippingMethodId
            if (basket?.shipments?.[0]?.shippingMethod?.id !== methodIdToUse) {
                // Update the shipping method only if it is different from the current one
                await adyenShippingMethodsService.updateShippingMethod(
                    methodIdToUse, // Prioritize the existing non bopis method),
                    basket.basketId
                )
            }
            if (countryISO && countryISO !== 'US') {
                await getGlobalePrices()
            }
            await queryClient.invalidateQueries({
                predicate: (query) =>
                    query.queryKey.some(
                        (segment) => typeof segment === 'string' && segment.includes('/baskets')
                    )
            })
        } catch (e) {
            console.error('Error occurred in changing the shipping information', e.message)
        } finally {
            setShowSpinner(false)
        }
        if (isBopis) {
            setDelivery('storepickup')
            await getBOPISAvailability()
        } else {
            setDelivery('delivery')
            await clearBopisFlag()
        }
        setShowPriceSkeleton(false)
        setRefreshPrices(true)
    }

    useEffect(() => {
        if (!currencyCode || sessionStorage.getItem('boltOAuthToken')) {
            return
        }
        const selectedShippingOption = basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled
            ? 'storepickup'
            : 'delivery'
        setDelivery(selectedShippingOption)
        if (
            basket?.shipments?.[0]?.shippingMethod?.id &&
            selectedShippingOption !== 'storepickup'
        ) {
            setShowPriceSkeleton(true)
            changeShippingInformation(selectedShippingOption === 'storepickup')
        }
        if (selectedShippingOption === 'storepickup') {
            getBOPISAvailability()
        } else {
            clearBopisFlag()
        }
    }, [
        basket?.shipments?.[0]?.shippingMethod?.id,
        store,
        currencyCode,
        basket?.couponItems?.length
    ])

    // The following will be execute only if new store is selected.
    useEffect(() => {
        if (sessionStorage.getItem('boltOAuthToken')) {
            return
        }
        if (refreshBopisCart && currencyCode) {
            setRefreshBopisCart(false)
            changeShippingInformation(refreshBopisCart || (currencyCode === 'US' ? true : false))
        }
    }, [refreshBopisCart, currencyCode])

    // The following will be execute when a product is edited in cart
    useEffect(() => {
        if (sessionStorage.getItem('boltOAuthToken')) {
            return
        }
        const selectedShippingOption = basket?.shipments?.[0]?.shippingMethod?.c_storePickupEnabled
            ? 'storepickup'
            : 'delivery'
        if (selectedShippingOption === 'storepickup') {
            getBOPISAvailability()
        }
    }, [basket?.productItems])

    const {
        app: {commerceAPI}
    } = getConfig()
    const {checkoutConfig} = commerceAPI
    const {configs} = checkoutConfig || {}

    let isBOPISEnabled = true

    try {
        if (configs) {
            const jsonConfig = JSON.parse(configs)
            isBOPISEnabled = jsonConfig.bopisEnabled
        }
    } catch (e) {
        console.error(e.message)
    }

    return (
        <Box {...cartStyles.deliveryMethod.container} data-storeid={store?.id || ''}>
            {showSpinner && <LoadingSpinner position="fixed" top="auto" />}
            <Text {...cartStyles.deliveryMethod.title}>
                <FormattedMessage
                    id="cart.delivery.method.title"
                    defaultMessage="Delivery Method"
                />
            </Text>
            {countryISO === 'US' ? (
                <Box {...cartStyles.deliveryMethod.deliveryOptions} className="delivery-option">
                    <Link
                        {...cartStyles.deliveryMethod.deliveryOptionText}
                        {...(selectedDelivery == 'delivery'
                            ? cartStyles.deliveryMethod.deliveryOptionTextSelected
                            : {})}
                        onClick={async (e) => {
                            e.preventDefault()
                            await changeShippingInformation(false)
                        }}
                        className="delivery-option-delivery"
                    >
                        <FormattedMessage id="cart.delivery.option" defaultMessage="Delivery" />
                    </Link>
                    {isBOPISEnabled && (
                        <Link
                            {...cartStyles.deliveryMethod.deliveryOptionText}
                            {...(selectedDelivery == 'storepickup'
                                ? cartStyles.deliveryMethod.deliveryOptionTextSelected
                                : {})}
                            onClick={async (e) => {
                                e.preventDefault()

                                if (!store) {
                                    onOpen()
                                } else {
                                    setisBopisLoading(true)
                                    await changeShippingInformation(true)
                                }
                            }}
                            className="delivery-option-storepickup"
                        >
                            <FormattedMessage
                                id="cart.storepickup.option"
                                defaultMessage="Free Store Pickup"
                            />
                        </Link>
                    )}
                </Box>
            ) : !countryISO && resolvedCountryISO === 'US' ? (
                <Skeleton height="46px" borderRadius="10px" />
            ) : null}
            <Box
                {...cartStyles.deliveryMethod.deliveryDetailsContainer}
                className="delivery-details"
            >
                {selectedDelivery == 'delivery' ? (
                    <Box
                        {...cartStyles.deliveryMethod.deliveryDetails.wrapper}
                        className="delivery-details-delivery"
                    >
                        <Image
                            src={getUrl('UIImages/images/delivery-method.svg')}
                            height="15px"
                            width="15px"
                            alt="Delivery Method"
                            {...cartStyles.deliveryMethod.deliveryDetails.image}
                        />
                        <Text as="span" {...cartStyles.deliveryMethod.deliveryDetails.delivery}>
                            <FormattedMessage id="cart.delivery.option" defaultMessage="Delivery" />
                        </Text>
                        <Text
                            as="span"
                            {...cartStyles.deliveryMethod.deliveryDetails.noDeliverySelectedLabel}
                        >
                            <FormattedMessage
                                id="cart.selec.option.in.checkout.label"
                                defaultMessage="Select options in Checkout"
                            />
                        </Text>
                    </Box>
                ) : (
                    <Box>
                        <Box
                            {...cartStyles.deliveryMethod.deliveryDetails.bopis}
                            className="delivery-details-bopis"
                        >
                            <Box {...cartStyles.deliveryMethod.deliveryDetails.imageTextWrapper}>
                                <Image
                                    src={getUrl('UIImages/images/delivery-method.svg')}
                                    height="15px"
                                    width="15px"
                                    alt="Delivery Method"
                                    marginTop="4px"
                                    {...cartStyles.deliveryMethod.deliveryDetails.image}
                                />
                                <Box
                                    {...cartStyles.deliveryMethod.deliveryDetails.bopisText}
                                    className="bopis-text"
                                >
                                    {oosBopisSkus?.length > 0 ? (
                                        <Text
                                            as="span"
                                            {...cartStyles.deliveryMethod.deliveryDetails
                                                .textLabelNotAvailable}
                                        >
                                            <FormattedMessage
                                                id="cart.free.store.pick-up.unavailable.label"
                                                defaultMessage="Unavailable for Buy Online, Pickup in Store at"
                                            />
                                        </Text>
                                    ) : (
                                        <Text
                                            as="span"
                                            {...cartStyles.deliveryMethod.deliveryDetails.textLabel}
                                        >
                                            <FormattedMessage
                                                id="cart.free.store.pick-up.label"
                                                defaultMessage="Buy Online, Pickup in Store at"
                                            />
                                        </Text>
                                    )}
                                    <Text
                                        as="span"
                                        {...cartStyles.deliveryMethod.deliveryDetails.selectedStore}
                                    >
                                        {store && 'name' in store ? store.name.toLowerCase() : ''}
                                    </Text>
                                </Box>
                            </Box>
                            <Link
                                id={oosBopisSkus?.length}
                                data-item={JSON.stringify(oosBopisSkus)}
                                onClick={(e) => {
                                    e.preventDefault()
                                    onOpen()
                                }}
                                {...cartStyles.deliveryMethod.deliveryDetails.changeStore}
                            >
                                <FormattedMessage
                                    id="cart.change.store.label"
                                    defaultMessage="Change Store"
                                />
                            </Link>
                        </Box>
                    </Box>
                )}
            </Box>
            {hasBopisShipmentWithEngravingProduct && (
                <Text
                    className="engraving-product-unavailable"
                    {...cartStyles.lineItems.item.bopisNotAvailable}
                >
                    <FormattedMessage
                        id="engraved.items.not.available"
                        defaultMessage="Engraved items are not available for pickup in store."
                    />
                </Text>
            )}
        </Box>
    )
}

CartDeliveryOptions.propTypes = {
    selectedDelivery: PropTypes.string,
    setDelivery: PropTypes.func,
    basket: PropTypes.any,
    setShowPriceSkeleton: PropTypes.func,
    getGlobalePrices: PropTypes.func,
    getBOPISAvailability: PropTypes.func,
    setisBopisLoading: PropTypes.func,
    clearBopisFlag: PropTypes.func,
    oosBopisSkus: PropTypes.array,
    hasBopisShipmentWithEngravingProduct: PropTypes.bool
}

export default CartDeliveryOptions
